import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, Snackbar, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import {
  Close as CloseIcon, LocalShipping as ShippingIcon, Replay as ResendIcon, PhotoCamera as CameraIcon,
  CheckCircle as CheckIcon, HourglassTop as WaitIcon, Lock as LockIcon, InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon, Mail as MailIcon, Inventory2 as PackageIcon
} from '@mui/icons-material';
import deliveryService from '../../services/deliveryService';
import { activeStepIndex, DELIVERY_STEPS, isValidEmail, qaChip, stageChip } from '../../utils/deliveryStages';
import { localTodayIso } from '../../utils/localDate';

const formatDateTime = (value) => (value ? new Date(value).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const formatDate = (value) => (value ? new Date(`${value}T12:00:00`).toLocaleDateString('es-MX', { dateStyle: 'medium' }) : '—');
const errorMessage = (e, fallback) => e?.response?.data?.error || e?.response?.data?.message || fallback;
const shortLabel = (label) => (label || '').split(' · ')[0];

const PANEL_SX = { bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, height: '100%' };

// Avance compacto: 6 puntos (Programada → Entregada), el nombre de cada etapa al pasar el mouse
const StageDots = ({ stage }) => {
  const active = activeStepIndex(stage);
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {DELIVERY_STEPS.map((s, i) => {
        const done = i < active;
        const current = i === active;
        return (
          <Tooltip key={s.stage} title={s.label}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%',
              bgcolor: done ? '#2e7d32' : current ? '#1976d2' : '#e0e0e0',
              boxShadow: current ? '0 0 0 3px rgba(25,118,210,0.2)' : 'none' }} />
          </Tooltip>
        );
      })}
    </Box>
  );
};

// Una sola lista: lo que lleva el paquete. PIPC, RIC, Carta y Acta siempre van (candado, d.required); las
// Constancias se marcan aquí mismo cuando se puede liberar.
const PackageList = ({ entity, editable, include, onToggle }) => {
  const docs = entity.documents || [];
  const optionalKey = { CARTA_CORRESPONSABILIDAD: 'carta', ACTA_CONSTITUTIVA_UIPC: 'acta' };
  const rows = [
    ...docs.map((d) => ({
      key: d.key,
      label: shortLabel(d.label) || d.label,
      fullLabel: d.label,
      version: d.available ? `v${d.version}` : '—',
      chip: qaChip(d),
      required: d.required,
      available: d.available,
      includeKey: optionalKey[d.key],
      approved: d.approvedBy ? `Aprobado por ${d.approvedBy} · ${formatDateTime(d.approvedAt)}` : null,
    })),
    {
      key: 'CONSTANCIAS', label: `Constancias${entity.constanciasCount > 0 ? ` (${entity.constanciasCount})` : ''}`,
      fullLabel: 'Constancias de capacitación', version: '—',
      chip: entity.constanciasCount > 0 ? { label: 'Generadas', color: 'success' } : { label: 'Sin constancias', color: 'default' },
      required: false, available: entity.constanciasCount > 0, includeKey: 'constancias',
    },
  ];
  return (
    <Box sx={PANEL_SX}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PackageIcon fontSize="small" color="action" /> Paquete a entregar
      </Typography>
      {rows.map((r) => {
        const canToggle = editable && !r.required && r.available && r.includeKey;
        return (
          <Box key={r.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: '1px solid #f0f0f0',
            opacity: r.available ? 1 : 0.55 }}>
            <Box sx={{ width: 34, display: 'flex', justifyContent: 'center' }}>
              {r.required ? (
                <Tooltip title="Siempre se entrega"><LockIcon sx={{ fontSize: 18, color: '#78909c' }} /></Tooltip>
              ) : canToggle ? (
                <Checkbox size="small" checked={!!include[r.includeKey]} onChange={(e) => onToggle(r.includeKey, e.target.checked)} sx={{ p: 0.5 }} />
              ) : <Box sx={{ width: 18 }} />}
            </Box>
            <Tooltip title={r.fullLabel}>
              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>{r.label}</Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ width: 28, textAlign: 'right' }}>{r.version}</Typography>
            <Chip size="small" label={r.chip.label} color={r.chip.color} variant={r.chip.color === 'default' ? 'outlined' : 'filled'}
              sx={{ minWidth: 118 }} />
            <Box sx={{ width: 22 }}>
              {r.approved && <Tooltip title={r.approved}><InfoIcon sx={{ fontSize: 18, color: '#90a4ae', cursor: 'help' }} /></Tooltip>}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const ReleasePanel = ({ entity, defaultEmail, include, onRelease, busy }) => {
  const [method, setMethod] = useState('PORTAL');
  const [email, setEmail] = useState(defaultEmail || '');
  const [message, setMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const docs = entity.documents || [];
  const carta = docs.find((d) => d.key === 'CARTA_CORRESPONSABILIDAD');
  const acta = docs.find((d) => d.key === 'ACTA_CONSTITUTIVA_UIPC');
  const includeCarta = !!(carta?.available && include.carta);
  const includeActa = !!(acta?.available && include.acta);
  const includeConstancias = !!(entity.constanciasCount > 0 && include.constancias);
  const emailOk = method === 'FISICA' || isValidEmail(email);
  // Lo que se va a liberar, con el mismo criterio de la lista "Paquete a entregar"
  const summary = [
    ...docs.filter((d) => d.required && d.available).map((d) => ({ label: d.label, version: `v${d.version}`, required: true })),
    ...(includeCarta ? [{ label: carta.label, version: `v${carta.version}` }] : []),
    ...(includeActa ? [{ label: acta.label, version: `v${acta.version}` }] : []),
    ...(includeConstancias ? [{ label: `Constancias de capacitación (${entity.constanciasCount})` }] : [])
  ];
  const count = docs.filter((d) => d.required && d.available).length + (includeCarta ? 1 : 0) + (includeActa ? 1 : 0)
    + (includeConstancias ? entity.constanciasCount : 0);

  const submit = () => {
    setConfirmOpen(false);
    onRelease({ deliveryMethod: method, notifyEmail: method === 'PORTAL' ? email.trim() : null, message, includeCarta, includeActa, includeConstancias });
  };

  return (
    <Box sx={{ ...PANEL_SX, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Cómo se entrega</Typography>
      <ToggleButtonGroup exclusive fullWidth size="small" value={method} onChange={(_, v) => v && setMethod(v)}>
        <ToggleButton value="PORTAL" sx={{ textTransform: 'none', fontWeight: 700, gap: 0.75 }}><MailIcon fontSize="small" /> Portal + correo</ToggleButton>
        <ToggleButton value="FISICA" sx={{ textTransform: 'none', fontWeight: 700, gap: 0.75 }}><ShippingIcon fontSize="small" /> Física</ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary">
        {method === 'PORTAL'
          ? 'Se publican en su portal y le llega un correo con la liga para descargarlos y confirmar de recibido.'
          : 'Se entrega impreso y después se registra el acuse firmado.'}
      </Typography>
      {method === 'PORTAL' && (
        <TextField fullWidth size="small" label="Avisar a (correo)" value={email} onChange={(e) => setEmail(e.target.value)}
          error={!emailOk} helperText={!emailOk ? 'Correo inválido' : 'Por omisión, el contacto corporativo'} />
      )}
      <TextField fullWidth size="small" multiline minRows={2} label="Mensaje para el cliente (opcional)" value={message}
        onChange={(e) => setMessage(e.target.value)} inputProps={{ maxLength: 1000 }} />
      <Box sx={{ flexGrow: 1 }} />
      <Button variant="contained" size="large" startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ShippingIcon />}
        disabled={!emailOk || busy || count === 0} onClick={() => setConfirmOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
        Liberar {count} documento{count === 1 ? '' : 's'} {method === 'PORTAL' ? 'por Portal + correo' : 'en entrega física'}
      </Button>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShippingIcon color="primary" />
          Liberar entrega · {entity.entityName}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, mb: 1.5, bgcolor: '#f5f7fb', borderRadius: 2 }}>
            {method === 'PORTAL' ? <MailIcon color="primary" sx={{ mt: 0.2 }} /> : <ShippingIcon color="primary" sx={{ mt: 0.2 }} />}
            <Box>
              <Typography variant="body2" fontWeight={700}>{method === 'PORTAL' ? 'Portal + correo' : 'Entrega física'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {method === 'PORTAL'
                  ? <>Se publicarán en el portal del cliente y se avisará a <b>{email}</b>.</>
                  : 'Después de entregarlos impresos, registra el acuse firmado.'}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>Se liberarán estos documentos:</Typography>
          {summary.map((item, idx) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
              borderBottom: idx < summary.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
              {item.required
                ? <Tooltip title="Siempre se entrega"><LockIcon sx={{ fontSize: 18, color: '#78909c' }} /></Tooltip>
                : <CheckIcon sx={{ fontSize: 18, color: '#4caf50' }} />}
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{item.label}</Typography>
              {item.version && <Chip size="small" label={item.version} variant="outlined" sx={{ height: 20, fontWeight: 700 }} />}
            </Box>
          ))}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Estas versiones quedarán <b>congeladas</b>. Si después se corrige algo, será una versión nueva con su propia revisión de calidad y entrega complementaria.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" startIcon={<ShippingIcon />} onClick={submit} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {method === 'PORTAL' ? 'Liberar y avisar al cliente' : 'Liberar entrega'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PhysicalAckDialog = ({ open, onClose, onSave, busy }) => {
  const [receivedBy, setReceivedBy] = useState('');
  const [deliveredOn, setDeliveredOn] = useState(localTodayIso());
  const [evidence, setEvidence] = useState(null);
  const tooBig = evidence && evidence.size > 15 * 1024 * 1024;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Registrar acuse físico</DialogTitle>
      <DialogContent>
        <TextField fullWidth margin="dense" label="Recibió (nombre)" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} />
        <TextField fullWidth margin="dense" type="date" label="Fecha de entrega" value={deliveredOn}
          onChange={(e) => setDeliveredOn(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ max: localTodayIso() }} />
        <Button component="label" variant="outlined" startIcon={<CameraIcon />} sx={{ mt: 1 }}>
          {evidence ? evidence.name : 'Acuse firmado (foto o PDF)'}
          <input hidden type="file" accept="image/jpeg,image/png,application/pdf" capture="environment" onChange={(e) => setEvidence(e.target.files?.[0] || null)} />
        </Button>
        {tooBig && <Alert severity="error" sx={{ mt: 1 }}>El archivo no puede pesar más de 15 MB</Alert>}
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>En celular abre la cámara directamente.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" color="success" disabled={!receivedBy.trim() || tooBig || busy}
          onClick={() => onSave({ receivedBy: receivedBy.trim(), deliveredOn, evidence })}>
          {busy ? <CircularProgress size={18} color="inherit" /> : 'Guardar acuse'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Panel derecho según el estado del ente: qué falta / cómo liberar / esperando acuse / entregado
const StatusPanel = ({ entity, defaultEmail, include, busy, onRelease, onResend, onOpenAck }) => {
  const pkg = entity.package;
  if (entity.stage === 'ENTREGADA' && pkg) {
    return (
      <Box sx={{ ...PANEL_SX, bgcolor: '#e8f5e9', borderColor: '#c8e6c9', display: 'flex', gap: 1.5 }}>
        <CheckIcon color="success" />
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Recibido de conformidad por {pkg.ackReceivedBy}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDateTime(pkg.ackAt)} · {pkg.ackMethod === 'FISICO' ? `acuse físico registrado por ${pkg.ackRecordedBy || '—'}` : 'confirmado desde el portal del cliente'}
            {pkg.hasEvidence ? ' · con evidencia' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">Liberada por {pkg.releasedBy || '—'} el {formatDateTime(pkg.releasedAt)}</Typography>
        </Box>
      </Box>
    );
  }
  if (entity.stage === 'LIBERADA' && pkg) {
    return (
      <Box sx={{ ...PANEL_SX, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WaitIcon fontSize="small" color="secondary" /> Liberada · esperando acuse
        </Typography>
        {pkg.deliveryMethod === 'PORTAL' ? (
          pkg.noticeSentAt
            ? <Alert severity="info">Se avisó a <b>{pkg.notifyEmail}</b> el {formatDateTime(pkg.noticeSentAt)}.</Alert>
            : <Alert severity="warning">Aún no se confirma el aviso a <b>{pkg.notifyEmail}</b>. Si en unos minutos sigue así, reenvíalo.</Alert>
        ) : <Alert severity="info">Entrega física: registra el acuse firmado.</Alert>}
        <Typography variant="caption" color="text.secondary">
          Liberada por {pkg.releasedBy || '—'} el {formatDateTime(pkg.releasedAt)}. Versiones congeladas: {pkg.items.map((i) => (i.version ? `${shortLabel(i.label)} v${i.version}` : i.label)).filter((v, i, a) => a.indexOf(v) === i).join(' · ')}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {pkg.deliveryMethod === 'PORTAL' && <Button variant="outlined" size="small" startIcon={<ResendIcon />} disabled={busy} onClick={onResend}>Reenviar por correo</Button>}
          <Button variant="outlined" size="small" startIcon={<CameraIcon />} disabled={busy} onClick={onOpenAck}>Registrar acuse físico</Button>
        </Box>
      </Box>
    );
  }
  if (entity.canRelease) {
    return <ReleasePanel entity={entity} defaultEmail={defaultEmail} include={include} busy={busy} onRelease={onRelease} />;
  }
  return (
    <Box sx={{ ...PANEL_SX, bgcolor: '#fff8e1', borderColor: '#ffe082' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {entity.stage === 'CANCELADA' ? 'Ente cancelado' : 'Todavía no se puede entregar'}
      </Typography>
      {entity.blockers?.length > 0 ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {entity.blockers.map((b) => <li key={b}><Typography variant="body2">{b}</Typography></li>)}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">{stageChip(entity.stage).label}</Typography>
      )}
    </Box>
  );
};

const EntityRow = ({ entity, defaultEmail, expanded, onToggleExpanded, onRelease, onResend, onPhysicalAck, busyId }) => {
  const [ackOpen, setAckOpen] = useState(false);
  const [include, setInclude] = useState({ carta: true, acta: true, constancias: true });
  const chip = stageChip(entity.stage);
  const busy = busyId === entity.detailId;
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, mb: 1.5, overflow: 'hidden' }}>
      <Box onClick={onToggleExpanded} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, cursor: 'pointer',
        flexWrap: 'wrap', '&:hover': { bgcolor: '#fafbfd' } }}>
        <ExpandMoreIcon sx={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', color: 'text.secondary' }} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {entity.entityName}
            <Chip size="small" label={entity.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal'} sx={{ ml: 1, height: 20 }} />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {entity.executive || 'Sin ejecutivo'} · visita {formatDate(entity.scheduledDate)}
          </Typography>
        </Box>
        {entity.stage !== 'CANCELADA' && <StageDots stage={entity.stage} />}
        <Chip label={chip.label} color={chip.color} variant={chip.color === 'default' ? 'outlined' : 'filled'} sx={{ fontWeight: 700 }} />
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 2, pt: 1, bgcolor: '#f8f9fc', borderTop: '1px solid #eee' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 7 }}>
              <PackageList entity={entity} editable={entity.canRelease} include={include}
                onToggle={(key, value) => setInclude((prev) => ({ ...prev, [key]: value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <StatusPanel entity={entity} defaultEmail={defaultEmail} include={include} busy={busy}
                onRelease={(payload) => onRelease(entity, payload)} onResend={() => onResend(entity)} onOpenAck={() => setAckOpen(true)} />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
      <PhysicalAckDialog open={ackOpen} busy={busy} onClose={() => setAckOpen(false)}
        onSave={async (data) => { const ok = await onPhysicalAck(entity, data); if (ok) setAckOpen(false); }} />
    </Box>
  );
};

// Filtros del resumen (chips): qué entes se muestran
const FILTERS = [
  { key: 'ALL', label: 'Entes', count: (s) => s.total, color: 'default', match: () => true },
  { key: 'DELIVERED', label: 'entregados', count: (s) => s.delivered, color: 'success', match: (e) => e.stage === 'ENTREGADA' },
  { key: 'READY', label: 'listos para entregar', count: (s) => s.ready, color: 'primary', match: (e) => e.stage === 'LISTA_PARA_ENTREGA' },
  { key: 'RELEASED', label: 'esperando acuse', count: (s) => s.released, color: 'secondary', match: (e) => e.stage === 'LIBERADA' },
  { key: 'REVIEW', label: 'en revisión de calidad', count: (s) => s.inReview, color: 'warning', match: (e) => e.stage === 'EN_REVISION' },
];

const EntregaClienteDialog = ({ open, workOrder, onClose, onChanged }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [snack, setSnack] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!workOrder?.id) return;
    setLoading(true);
    try {
      setData(await deliveryService.overview(workOrder.id));
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'No se pudo cargar la información de entrega'));
    } finally {
      setLoading(false);
    }
  }, [workOrder]);

  useEffect(() => {
    if (open) {
      setFilter('ALL');
      setExpanded(null);
      load();
    }
  }, [open, load]);

  // Abre solo el primer ente que está listo (o el primero sin entregar); el resto cerrado
  useEffect(() => {
    if (!data || expanded !== null) return;
    const first = data.entities.find((e) => e.canRelease) || data.entities.find((e) => e.stage !== 'ENTREGADA') || data.entities[0];
    if (first) setExpanded(new Set([first.detailId]));
  }, [data, expanded]);

  const toggleExpanded = (detailId) => setExpanded((prev) => {
    const next = new Set(prev || []);
    if (next.has(detailId)) next.delete(detailId); else next.add(detailId);
    return next;
  });

  const run = async (entity, action, okMessage) => {
    setBusyId(entity.detailId);
    try {
      const result = await action();
      setSnack({ severity: 'success', message: typeof okMessage === 'function' ? okMessage(result) : okMessage });
      await load();
      if (onChanged) onChanged();
      return true;
    } catch (e) {
      setSnack({ severity: 'error', message: errorMessage(e, 'No se pudo completar la acción') });
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const handleRelease = (entity, payload) => run(entity, () => deliveryService.release(entity.detailId, payload),
    (r) => (payload.deliveryMethod === 'PORTAL'
      ? (r.noticeQueued ? `Entrega liberada. El aviso va en camino a ${payload.notifyEmail}.` : 'Entrega liberada.')
      : 'Entrega liberada. Registra el acuse físico cuando se entregue.'));
  const handleResend = (entity) => run(entity, () => deliveryService.resendNotice(entity.package.id), 'Aviso en camino; llegará en unos minutos.');
  const handlePhysicalAck = (entity, ack) => run(entity, () => deliveryService.physicalAck(entity.package.id, ack), 'Acuse registrado. El ente quedó entregado.');

  const summary = data?.summary || {};
  const activeFilter = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const visible = (data?.entities || []).filter(activeFilter.match);
  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <ShippingIcon color="primary" />
        <Box>
          <Typography variant="h6" component="span">Entrega al cliente · {data?.companyName || workOrder?.companyName || ''}</Typography>
          <Typography variant="caption" display="block" color="text.secondary">{data?.orderNumber || workOrder?.orderNumber} · cada ente se entrega por separado; la OT se completa sola al entregar todos.</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#f5f7fb' }}>
        {loading && !data && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {data && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <Chip key={f.key} clickable onClick={() => setFilter(f.key)}
                    label={`${f.count(summary) ?? 0} ${f.label}`}
                    color={active ? (f.color === 'default' ? 'primary' : f.color) : 'default'}
                    variant={active ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
                );
              })}
            </Box>
            {data.workOrderStatus === 'COMPLETADA' && <Alert severity="success" sx={{ mb: 2 }}>La orden de trabajo está completada: todos sus entes fueron entregados.</Alert>}
            {visible.map((entity) => (
              <EntityRow key={entity.detailId} entity={entity} defaultEmail={data.defaultNotifyEmail} busyId={busyId}
                expanded={!!expanded?.has(entity.detailId)} onToggleExpanded={() => toggleExpanded(entity.detailId)}
                onRelease={handleRelease} onResend={handleResend} onPhysicalAck={handlePhysicalAck} />
            ))}
            {data.entities.length === 0 && <Alert severity="info">Esta orden de trabajo no tiene entes.</Alert>}
            {data.entities.length > 0 && visible.length === 0 && (
              <Alert severity="info">Ningún ente en «{activeFilter.label}». <Button size="small" onClick={() => setFilter('ALL')}>Ver todos</Button></Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Tooltip title="Volver a consultar el estado"><span><Button onClick={load} disabled={loading}>Actualizar</Button></span></Tooltip>
        <Button variant="contained" onClick={onClose}>Cerrar</Button>
      </DialogActions>
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : <span />}
      </Snackbar>
    </Dialog>
  );
};

export default EntregaClienteDialog;
